import Mention from "@tiptap/extension-mention"

import { component_is_an_alternative } from "core/data/component_is"
import { IdAndMaybeVersion, IdAndVersion, IdOnly, parse_id } from "core/data/id"
import { DataComponent } from "core/data/interface"

import AlternativeSVG from "../assets/alternative.svg"
import UpdateVersionSVG from "../assets/update_version.svg"
import pub_sub from "../pub_sub"
import { get_currently_pressed_keys } from "../pub_sub/publish_key_down_events"
import { ROUTES } from "../routes"
import "./CustomReferences.css"
import { get_shared_app_state } from "./CustomReferences_state"



// Custom mention extension with chip functionality
const CustomMention = Mention.extend({
    name: "customMention",

    parseHTML()
    {
        return [
            {
                tag: "a.mention-chip",
            },
            {
                tag: "span.mention-chip",
            }
        ]
    },

    addAttributes()
    {
        return {
            id: {
                default: null,
                // This is used to parse out the ID from the HTML element and
                // set the `node.attrs.id` use in the addNodeView function.
                parseHTML: element => element.getAttribute("data-id"),
                // This will be set in the `HTMLAttributes` passed to the
                // renderHTML function
                renderHTML: attributes => ({ "data-id": attributes.id as string }),
            },
            label: {
                default: null,
                // This is used to parse out the ID from the HTML element and
                // set the `node.attrs.id` use in the addNodeView function.
                // Older span based mentions may have had an @ symbol prepended
                // so we remove it here.
                parseHTML: el => el.textContent?.replace(/^@/, "") || "",
                // Don't emit data-label; label will be rendered as inner text
                renderHTML: () => ({}),
            },
            // Override the default mention extension to NOT include mention suggestion char
        }
    },


    addNodeView()
    {
        return ({ node }) =>
        {
            const parsed_id = parse_id_safely(node.attrs.id as string)
            if (!parsed_id) return { dom: document.createElement("span") }

            const dom = document.createElement("a")
            dom.className = `mention-chip ${parsed_id instanceof IdOnly ? "IDo" : "IDv"}`
            const dangerous_str = `${node.attrs.label || ""}`
            // Use textContent instead of innerHTML to prevent XSS
            dom.textContent = dangerous_str
            dom.href = ROUTES.DATA_COMPONENT.VIEW_WIKI_COMPONENT(parsed_id)


            const is_an_alternative_according_to = get_component_is_an_alternative_according_to(parsed_id)
            if (is_an_alternative_according_to)
            {
                const warning_icon = document.createElement("img")
                warning_icon.src = AlternativeSVG
                warning_icon.alt = "Is alternative"
                warning_icon.title = (
                    "This is an alternative"
                    + (is_an_alternative_according_to === true ? "." : ` according to ${is_an_alternative_according_to.plain_title}`)
                )
                warning_icon.width = 14
                warning_icon.height = 14
                warning_icon.style.marginLeft = "4px"
                dom.appendChild(warning_icon)
            }


            // Check if component version is latest, if not add a warning icon and tooltip
            const newer_component = check_if_component_version_is_old(parsed_id)
            if (newer_component)
            {
                const warning_icon = document.createElement("img")
                warning_icon.src = UpdateVersionSVG
                warning_icon.alt = "Update Version"
                warning_icon.title = "A newer version of this component exists"
                warning_icon.width = 14
                warning_icon.height = 14
                warning_icon.style.marginLeft = "4px"
                dom.appendChild(warning_icon)
            }

            // // label_span.textContent = dangerous_str
            // // dom.appendChild(label_span)

            // // const percentage = "+20%"
            // // dom.className += " alternative-value"

            // Custom chip click behaviour
            dom.addEventListener("click", e => {
                e.stopImmediatePropagation()

                // On Mac OS, when a user holds the cmd key and clicks a link, they
                // expect it to open in a new tab.  Don't interfere with that default behavior.
                if (get_currently_pressed_keys().metaKey) return

                // If we're letting the click event proceed to cause the page
                // navigation then we need to prevent default behaviour which
                // would otherwise cause a full page reload.
                e.preventDefault()
                pub_sub.pub("mention_clicked", { data_component_id: node.attrs.id as string })
            })

            return { dom }
        }
    },


    renderHTML({ HTMLAttributes, node })
    {
        if (!HTMLAttributes["data-id"]) console.warn("CustomMention renderHTML missing data-id attribute", HTMLAttributes)
        const label = (node.attrs.label as string || "")

        return [
            "a",
            { ...HTMLAttributes, class: "mention-chip" },
            label
        ]
    },


    addKeyboardShortcuts()
    {
        return {
            Backspace: ({ editor }) =>
            {
                const { selection, doc } = editor.state

                // Check if we are at the position right after a mention node
                if (selection.empty)
                {
                    const $pos = doc.resolve(selection.from)
                    const before = $pos.nodeBefore

                    if (before && before.type.name === "customMention")
                    {
                        // Delete only the mention node
                        const mentionStart = selection.from - before.nodeSize
                        return editor.commands.deleteRange({ from: mentionStart, to: selection.from })
                    }
                }

                return false
            },
            Delete: ({ editor }) =>
            {
                const { selection, doc } = editor.state

                // Check if we are at the position right before a mention node
                if (selection.empty)
                {
                    const $pos = doc.resolve(selection.from)
                    const after = $pos.nodeAfter

                    if (after && after.type.name === "customMention")
                    {
                        // Delete only the mention node
                        const mentionEnd = selection.from + after.nodeSize
                        return editor.commands.deleteRange({ from: selection.from, to: mentionEnd })
                    }
                }

                return false
            }
        }
    },
})



export const CustomReferences = CustomMention.configure({
    HTMLAttributes: {
        class: "mention-chip",
    },
})


function parse_id_safely(id_str: string): IdAndMaybeVersion | undefined
{
    try
    {
        return parse_id(id_str)
    }
    catch (_)
    {
        return undefined
    }
}


function get_component_is_an_alternative_according_to(component_id: IdAndMaybeVersion): DataComponent | boolean | undefined
{
    const root_app_state = get_shared_app_state()
    if (!root_app_state) return undefined

    const components_by_id = root_app_state.data_components.data_component_by_id_and_maybe_version

    const async_component = components_by_id[component_id.to_str_without_version()]
    if (!async_component) return undefined

    const { component } = async_component
    if (!component_is_an_alternative(component)) return false

    const async_alternative = components_by_id[component.according_to_id]
    if (!async_alternative) return true
    const { component: alternative_component } = async_alternative
    return alternative_component ?? true
}


function check_if_component_version_is_old(component_id: IdAndMaybeVersion): DataComponent | false | undefined
{
    if (!(component_id instanceof IdAndVersion)) return undefined

    const root_app_state = get_shared_app_state()
    if (!root_app_state) return undefined

    const components_by_id = root_app_state.data_components.data_component_by_id_and_maybe_version

    const async_component = components_by_id[component_id.to_str_without_version()]
    if (!async_component) return undefined

    const { component } = async_component
    const latest_version = component?.id.version
    if (component && latest_version !== component_id.version)
    {
        return component
    }

    return false
}
